import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  CircularProgress
} from '@mui/material';
import {
  Add,
  ArrowUpward,
  UploadFile,
  Screenshot,
  CloudUpload,
  ExpandMore,
  Inventory
} from '@mui/icons-material';
import { useChatContext } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import FilePreviewModal from './FilePreviewModal';
import FileProgressOverlay from './FileProgressOverlay';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { usePasteHandler } from '../hooks/usePasteHandler';
import { validateFileType } from '../utils/fileUtils';
import ValidationMessage from './ValidationMessage';
import PromptVaultDropdown from './PromptVaultDropdown';

  // console.log('LandingView rendered');

const LandingView: React.FC = () => {
  const {
    uiState,
    appState,
    sendMessage,
    addAttachedFile,
    removeAttachedFile,
    setSelectedExpert,
    showError,
    hasUploadsInProgress,
    getFileUploadProgress,
    isFileUploadComplete,
    areAllUploadsComplete,
    validateCurrentMessage,
    clearValidationError
  } = useChatContext();

  const { user } = useAuth();

  // console.log('LandingView state', { uiState, appState });

  const [message, setMessage] = useState('');
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [expertsMenuOpen, setExpertsMenuOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileCardsContainerRef = useRef<HTMLDivElement>(null);
  
  // PromptVault states
  const [promptVaultDropdownOpen, setPromptVaultDropdownOpen] = useState(false);

  // Blob URL cache to prevent excessive creation
  const blobUrlCacheRef = useRef<{ [key: string]: string }>({});
  
  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(blobUrlCacheRef.current).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      blobUrlCacheRef.current = {};
    };
  }, []);

  // Memoized blob URLs for attached files
  const attachedFileBlobUrls = useMemo(() => {
    const urls: { [key: string]: string } = {};
    const oldCache = blobUrlCacheRef.current;
    const usedKeys = new Set<string>();
    
    uiState.attachedFiles.forEach((file, index) => {
      const key = `attached-${file.name}-${file.size}-${index}`;
      usedKeys.add(key);
      
      // Reuse existing blob URL if it exists for the same file
      if (oldCache[key]) {
        urls[key] = oldCache[key];
      } else {
        // Only create new blob URL if we don't have one
        urls[key] = URL.createObjectURL(file);
      }
    });
    
    // Clean up unused blob URLs
    Object.keys(oldCache).forEach(key => {
      if (!usedKeys.has(key) && oldCache[key].startsWith('blob:')) {
        URL.revokeObjectURL(oldCache[key]);
      }
    });
    
    // Update cache
    blobUrlCacheRef.current = urls;
    return urls;
  }, [uiState.attachedFiles]);

  // Drag and drop functionality
  const handleFileDrop = async (files: File[]) => {
    const validFiles: File[] = [];
    
    // First, validate all file types
    for (const file of files) {
      const result = await validateFileType(file);
      if (result.valid) {
        validFiles.push(file);
      } else {
        showError(result.reason || 'Invalid file type.');
        return; // Stop processing if any file is invalid
      }
    }
    
    // Add all valid files
    for (const file of validFiles) {
      addAttachedFile(file);
    }
    
    // If files were added and we have an expert selected, validate immediately
    // This ensures O3 image size validation happens right after drop
    if (validFiles.length > 0 && uiState.selectedExpert) {
      // Use requestAnimationFrame to ensure all files are added to state before validation
      requestAnimationFrame(async () => {
        await validateCurrentMessage(message);
      });
    }
  };

  const { isDragOver } = useDragAndDrop({
    onDrop: handleFileDrop,
    isVisible: true
  });

  // Paste handler functionality
  const { handlePaste, resetCounters } = usePasteHandler({
    onFileAttach: addAttachedFile
  });

  // Set up paste event listener
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener('paste', handlePaste);
      return () => {
        textarea.removeEventListener('paste', handlePaste);
      };
    }
  }, [handlePaste]);

  // Focus textarea only after initialization is complete (experts loaded and no loading states active)
  useEffect(() => {
    if (textareaRef.current && 
        !uiState.showLoading && 
        !appState.isLoadingChats && 
        appState.filteredExperts && 
        appState.filteredExperts.length > 0) {
      textareaRef.current.focus();
    }
  }, [uiState.showLoading, appState.isLoadingChats, appState.filteredExperts]);

  // Validate message when text or files change
  useEffect(() => {
    const validateMessage = async () => {
      if (message.trim() || uiState.attachedFiles.length > 0) {
        await validateCurrentMessage(message);
      } else {
        clearValidationError();
      }
    };

    const debounceTimeout = setTimeout(validateMessage, 150);
    return () => clearTimeout(debounceTimeout);
  }, [message, uiState.attachedFiles, validateCurrentMessage, clearValidationError]);

  // Auto-resize textarea functionality with dynamic height sharing
  useEffect(() => {
    const textarea = textareaRef.current;
    const fileCardsContainer = fileCardsContainerRef.current;
    if (!textarea) return;

    const autoResize = () => {
      // Reset height to auto to get the actual scroll height
      textarea.style.height = 'auto';
      const desiredHeight = textarea.scrollHeight;
      
      // Constants for dynamic height behavior
      const FILE_CARDS_MAX_HEIGHT = 412; // Default max height from inline styles
      const FILE_CARDS_MIN_HEIGHT = 110; // Minimum height per user requirement
      const AVAILABLE_SPACE_TO_TRADE = FILE_CARDS_MAX_HEIGHT - FILE_CARDS_MIN_HEIGHT; // 302px
      const TEXTAREA_BASE_HEIGHT = 48; // Base height for landing view
      
      // Calculate how much extra space the textarea wants
      const extraHeightNeeded = Math.max(0, desiredHeight - TEXTAREA_BASE_HEIGHT);
      
      if (fileCardsContainer && uiState.attachedFiles.length > 0) {
        // We have attached files, so we can trade space
        const spaceToTakeFromFiles = Math.min(extraHeightNeeded, AVAILABLE_SPACE_TO_TRADE);
        const newFileCardsHeight = FILE_CARDS_MAX_HEIGHT - spaceToTakeFromFiles;
        
        // Update file cards container height
        fileCardsContainer.style.height = newFileCardsHeight + 'px';
        
        if (extraHeightNeeded <= AVAILABLE_SPACE_TO_TRADE) {
          // We can accommodate the expansion by reducing file cards area
          textarea.style.setProperty('height', `${desiredHeight}px`, 'important');
          textarea.style.setProperty('overflow-y', 'hidden', 'important');
          textarea.removeAttribute('data-at-max-height');
        } else {
          // We've used all available space from file cards, now check viewport constraints
          const inputBox = textarea.closest('.input-box-styling') as HTMLElement;
          if (inputBox) {
            const inputBoxRect = inputBox.getBoundingClientRect();
            const inputBoxTop = inputBoxRect.top;
            const inputBoxStyles = window.getComputedStyle(inputBox);
            const topPadding = parseInt(inputBoxStyles.paddingTop) || 16;
            const bottomPadding = parseInt(inputBoxStyles.paddingBottom) || 56;
            
            // Calculate viewport constraints
            const maxAllowedBottom = window.innerHeight - 80;
            const maxTextareaHeightFromViewport = maxAllowedBottom - inputBoxTop - topPadding - bottomPadding;
            const maxTextareaHeightFromFiles = TEXTAREA_BASE_HEIGHT + AVAILABLE_SPACE_TO_TRADE;
            
            // Use the smaller of the two constraints
            const finalMaxHeight = Math.min(maxTextareaHeightFromViewport, maxTextareaHeightFromFiles);
            const clampedHeight = Math.max(48, finalMaxHeight);
            
            textarea.style.setProperty('height', `${clampedHeight}px`, 'important');
            textarea.style.setProperty('overflow-y', 'auto', 'important');
            textarea.setAttribute('data-at-max-height', 'true');
          } else {
            // Fallback if we can't find input box
            const maxTextareaHeight = TEXTAREA_BASE_HEIGHT + AVAILABLE_SPACE_TO_TRADE;
            textarea.style.setProperty('height', `${maxTextareaHeight}px`, 'important');
            textarea.style.setProperty('overflow-y', 'auto', 'important');
            textarea.setAttribute('data-at-max-height', 'true');
          }
        }
      } else {
        // No attached files, use viewport-based behavior (original logic)
        const inputBox = textarea.closest('.input-box-styling') as HTMLElement;
        if (inputBox) {
          const inputBoxRect = inputBox.getBoundingClientRect();
          const inputBoxTop = inputBoxRect.top;
          const inputBoxStyles = window.getComputedStyle(inputBox);
          const topPadding = parseInt(inputBoxStyles.paddingTop) || 16;
          const bottomPadding = parseInt(inputBoxStyles.paddingBottom) || 56;
          
          const totalInputBoxHeight = topPadding + desiredHeight + bottomPadding;
          const inputBoxBottom = inputBoxTop + totalInputBoxHeight;
          const maxAllowedBottom = window.innerHeight - 80;
          
          if (inputBoxBottom > maxAllowedBottom) {
            const availableSpaceForInputBox = maxAllowedBottom - inputBoxTop;
            const maxTextareaHeight = availableSpaceForInputBox - topPadding - bottomPadding;
            const clampedHeight = Math.max(48, maxTextareaHeight);
            
            textarea.style.setProperty('height', `${clampedHeight}px`, 'important');
            textarea.style.setProperty('overflow-y', 'auto', 'important');
            textarea.setAttribute('data-at-max-height', 'true');
          } else {
            textarea.style.setProperty('height', `${desiredHeight}px`, 'important');
            textarea.style.setProperty('overflow-y', 'hidden', 'important');
            textarea.removeAttribute('data-at-max-height');
          }
        }
      }
    };

    // Auto-resize on input
    textarea.addEventListener('input', autoResize);
    
    // Initial resize with a small delay to ensure DOM is fully rendered
    const initialResize = () => setTimeout(autoResize, 10);
    initialResize();
    
    // Resize on window resize
    const handleWindowResize = () => {
      setTimeout(autoResize, 10); // Small delay to ensure layout has updated
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      textarea.removeEventListener('input', autoResize);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [message, uiState.attachedFiles.length]); // Re-run when message changes

  // Reset file cards container height when no files are attached
  useEffect(() => {
    const fileCardsContainer = fileCardsContainerRef.current;
    if (fileCardsContainer && uiState.attachedFiles.length === 0) {
      // Reset to default max height when no files are attached
      fileCardsContainer.style.height = '412px';
    }
  }, [uiState.attachedFiles.length]);

  const handleSubmit = async () => {
    if (!message.trim() || !uiState.selectedExpert) return;
    
    // Validate message before submission
    const validationResult = await validateCurrentMessage(message);
    if (!validationResult.isValid) {
      return; // Don't submit if validation fails
    }
    
    // Immediately switch to chat view and optimistically add user/placeholder messages
    await sendMessage(message, uiState.attachedFiles, true);
    setMessage('');
    resetCounters();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleAttachClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAttachMenuOpen((prev) => !prev);
  };

  const handleAttachClose = () => {
    setAttachMenuOpen(false);
    // Remove focus from the attach button to prevent the black border
    const attachButton = document.querySelector('.attach-button') as HTMLButtonElement;
    if (attachButton) {
      attachButton.blur();
    }
  };

  const handleExpertsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpertsMenuOpen((prev) => !prev);
  };

  const handleExpertsClose = () => {
    setExpertsMenuOpen(false);
    // Remove focus from the expert button to prevent the black border
    const expertButton = document.querySelector('.experts-dropdown-btn') as HTMLButtonElement;
    if (expertButton) {
      expertButton.blur();
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
    handleAttachClose();
  };

  const handleScreenshot = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        showError('Screen capture is not supported in this browser.');
        handleAttachClose();
        return;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true
      });

      // Create a video element to capture the frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      video.addEventListener('loadedmetadata', () => {
        // Create canvas to capture the frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.drawImage(video, 0, 0);
          
          // Convert to blob and create file
          canvas.toBlob((blob) => {
            if (blob) {
              const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
              const file = new File([blob], `screenshot-${timestamp}.png`, { type: 'image/png' });
              addAttachedFile(file);
            }
            
            // Stop the stream
            stream.getTracks().forEach(track => track.stop());
          }, 'image/png');
        } else {
          stream.getTracks().forEach(track => track.stop());
          showError('Failed to capture screenshot.');
        }
      });

      handleAttachClose();
    } catch (error) {
      console.error('Screenshot error:', error);
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          showError('Screen capture permission was denied.');
        } else if (error.name === 'NotSupportedError') {
          showError('Screen capture is not supported in this browser.');
        } else {
          showError('Failed to capture screenshot: ' + error.message);
        }
      } else {
        showError('Failed to capture screenshot.');
      }
      handleAttachClose();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      for (const file of Array.from(files)) {
        const result = await validateFileType(file);
        if (result.valid) {
          addAttachedFile(file);
        } else {
          showError(result.reason || 'Invalid file type.');
          break;
        }
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    removeAttachedFile(index);
    
    // Re-evaluate validation immediately after file removal if we have an expert selected
    // This ensures validation errors are cleared right away when files are removed to fix issues
    if (uiState.selectedExpert) {
      // Use requestAnimationFrame to ensure the file is removed from state first
      requestAnimationFrame(async () => {
        await validateCurrentMessage(message);
      });
    }
  };

  const handleFilePreview = (file: File) => {
    setPreviewFile(file);
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewFile(null);
  };

  // PromptVault handlers
  const handlePromptVaultToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setPromptVaultDropdownOpen(!promptVaultDropdownOpen);
  };

  const handlePromptVaultClose = () => {
    setPromptVaultDropdownOpen(false);
  };

  const handleSelectPrompt = (content: string) => {
    setMessage(content);
    setPromptVaultDropdownOpen(false);
    // Auto-resize the textarea
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };



  const isSubmitDisabled = !message.trim() || !uiState.selectedExpert || appState.isProcessing || hasUploadsInProgress() || uiState.showValidationError;

  // Close experts dropdown if clicking outside
  React.useEffect(() => {
    if (!expertsMenuOpen) return;
    const handleDocumentClick = (e: MouseEvent) => {
      const dropdown = document.getElementById('custom-experts-dropdown');
      const expertButton = document.querySelector('.experts-dropdown-btn');
      
      // Don't close if clicking on the dropdown itself or the expert button
      if (dropdown && !dropdown.contains(e.target as Node) && 
          expertButton && !expertButton.contains(e.target as Node)) {
        setExpertsMenuOpen(false);
      }
    };
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && expertsMenuOpen) {
        setExpertsMenuOpen(false);
        // Remove focus from the expert button to prevent the black border
        const expertButton = document.querySelector('.experts-dropdown-btn') as HTMLButtonElement;
        if (expertButton) {
          expertButton.blur();
        }
      }
    };
    
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [expertsMenuOpen]);

  // Close attach dropdown if clicking outside
  React.useEffect(() => {
    if (!attachMenuOpen) return;
    const handleDocumentClick = (e: MouseEvent) => {
      const dropdown = document.getElementById('custom-attach-dropdown');
      const attachButton = document.querySelector('.attach-button');
      
      // Don't close if clicking on the dropdown itself or the attach button
      if (dropdown && !dropdown.contains(e.target as Node) && 
          attachButton && !attachButton.contains(e.target as Node)) {
        setAttachMenuOpen(false);
      }
    };
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && attachMenuOpen) {
        setAttachMenuOpen(false);
        // Remove focus from the attach button to prevent the black border
        const attachButton = document.querySelector('.attach-button') as HTMLButtonElement;
        if (attachButton) {
          attachButton.blur();
        }
      }
    };
    
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [attachMenuOpen]);

  // Function to extract first name from display name
  const getFirstName = () => {
    if (!user?.userDisplayName) return 'User';
    
    // Split by space and take the first part
    const nameParts = user.userDisplayName.trim().split(' ');
    return nameParts[0] || 'User';
  };

  return (
    <div className="landing-container">
      {/* Welcome Header */}
      <h1 className="landing-title">
        Welcome, {getFirstName()}
      </h1>

      {/* Input Container */}
      <div style={{ width: '100%' }}>
        {/* Validation Message */}
        <ValidationMessage
          isVisible={uiState.showValidationError}
          errorMessage={uiState.validationError || undefined}
        />
        
        <div className="input-box-styling" style={{ position: 'relative' }}>
          {/* Drag and Drop Overlay */}
          {isDragOver && (
            <div className="landing-drag-area">
              <div className="drag-content">
                <CloudUpload sx={{ fontSize: 48 }} />
                <p>Drop files here to upload</p>
              </div>
            </div>
          )}

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Bell a question.&#10;Press Enter to submit, Shift+Enter for a new line."
            ref={textareaRef}
            style={{
              minHeight: '48px',
              height: 'auto'
            }}
            disabled={appState.isProcessing}
          />

          {/* Controls Row */}
          <div className="input-box-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'absolute', bottom: 8, left: 8, right: 8 }}>
            {/* Left side - Attach button and PromptVault button */}
            <div className="input-box-left" style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                className="attach-button"
                onClick={handleAttachClick}
                type="button"
                disabled={appState.isProcessing}
                title="Attach..."
              >
                <Add />
              </button>
              {attachMenuOpen && (
                <div
                  id="custom-attach-dropdown"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    minWidth: 180,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    zIndex: 3000,
                    marginTop: 4,
                    padding: 0
                  }}
                >
                  <button
                    className="attach-menu-item"
                    onClick={handleFileUpload}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#333',
                      borderRadius: '6px',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.background = 'none';
                    }}
                  >
                    <UploadFile sx={{ fontSize: 16 }} />
                    Upload a File
                  </button>
                  <button
                    className="attach-menu-item"
                    onClick={handleScreenshot}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      color: '#333',
                      borderRadius: '6px',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.background = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.background = 'none';
                    }}
                  >
                    <Screenshot sx={{ fontSize: 16 }} />
                    Take a Screenshot
                  </button>
                </div>
              )}
              
              {/* PromptVault Button */}
              <div style={{ position: 'relative' }}>
                <button
                  className="prompt-vault-button"
                  onClick={handlePromptVaultToggle}
                  type="button"
                  disabled={appState.isProcessing || appState.isLoadingPromptVault}
                  title={appState.isLoadingPromptVault ? "Loading PromptVault..." : "Load from PromptVault"}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: appState.isLoadingPromptVault ? 'default' : 'pointer',
                    color: '#666',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (!appState.isLoadingPromptVault) {
                      (e.target as HTMLElement).style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  }}
                >
                  {appState.isLoadingPromptVault ? (
                    <CircularProgress size={20} style={{ color: '#666' }} />
                  ) : (
                    <Inventory />
                  )}
                </button>
                              {promptVaultDropdownOpen && (
                <PromptVaultDropdown
                  open={promptVaultDropdownOpen}
                  onClose={handlePromptVaultClose}
                  onSelectPrompt={handleSelectPrompt}
                />
              )}
              </div>
            </div>
            
            {/* Right side - Expert button and Submit button */}
            <div className="input-box-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Expert selection button */}
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className="experts-dropdown-btn"
                  onClick={handleExpertsClick}
                  type="button"
                  disabled={appState.isProcessing}
                >
                  <span>{uiState.selectedExpert?.name || uiState.selectedExpert?.archetype || 'Choose Expert'}</span>
                  <ExpandMore />
                </button>
                {expertsMenuOpen && (
                  <div
                    id="custom-experts-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      minWidth: 320,
                      maxHeight: '400px',
                      overflowY: 'auto',
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      zIndex: 3000,
                      marginTop: 4,
                      padding: 0
                    }}
                  >
                    {appState.filteredExperts?.length > 0 ? appState.filteredExperts.map(expert => {
                      const expertIdentifier = expert.name || expert.archetype || expert.id || expert.ID;
                      const selectedExpertIdentifier = uiState.selectedExpert?.name || uiState.selectedExpert?.archetype || uiState.selectedExpert?.id || uiState.selectedExpert?.ID;
                      const expertName = expert.name || expert.archetype || expert.ARCHETYPE || '(Unnamed Expert)';
                      const expertDesc = expert.description || expert.DESCRIPTION || '';
                      const fullTooltip = `${expertName}\n\n${expertDesc}`;
                      return (
                        <div
                          key={expertIdentifier}
                          className={`expert-card${selectedExpertIdentifier === expertIdentifier ? ' selected' : ''}`}
                          style={{ cursor: 'pointer' }}
                          title={fullTooltip}
                          onClick={(e) => { 
                            e.stopPropagation();
                            setSelectedExpert(expert);
                            handleExpertsClose(); 
                          }}
                        >
                          <div className="expert-details">
                            <div className="expert-name">{expertName}</div>
                            <div className="expert-description">{expertDesc}</div>
                          </div>
                        </div>
                      );
                    }) : (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#888' }}>
                        No experts available
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Submit button */}
              <button
                className="submit-button"
                onClick={handleSubmit}
                type="button"
                disabled={isSubmitDisabled}
              >
                <ArrowUpward />
              </button>
            </div>
          </div>
        </div>

        {/* Attached Files - Render as cards below the input area */}
        {uiState.attachedFiles.length > 0 && (
          <div className="file-cards-container" ref={fileCardsContainerRef} style={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap', height: '412px', overflowY: 'auto', width: '96%', margin: 'auto' }}>
            {uiState.attachedFiles.map((file, index) => {
              // Get file extension for type label
              let ext = file.name.split('.').pop()?.toUpperCase() || 'FILE';
              if (ext === file.name) ext = 'FILE';
              
              // Check if this is an image file
              const isImageFile = file.type?.startsWith('image/') || 
                ['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP', 'BMP', 'SVG'].includes(ext);
              
              if (isImageFile) {
                return (
                  <div 
                    key={index} 
                    className="file-card image-card" 
                    tabIndex={0} 
                    role="button" 
                    aria-label={`Preview ${file.name}`} 
                    data-filename={file.name}
                    onClick={() => handleFilePreview(file)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFilePreview(file);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      className="file-card-img" 
                      src={attachedFileBlobUrls[`attached-${file.name}-${file.size}-${index}`]} 
                      alt={file.name} 
                      data-filename={file.name} 
                    />
                    {(() => {
                      const progress = getFileUploadProgress(file.name);
                      const isComplete = isFileUploadComplete(file.name);
                      const showOverlay = progress >= 1 && (!areAllUploadsComplete() || progress < 100);
                      return showOverlay && (
                        <FileProgressOverlay 
                          progress={progress} 
                          isComplete={isComplete} 
                          fileName={file.name} 
                        />
                      );
                    })()}
                    <button
                      className="file-card-close-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </button>
                  </div>
                );
              } else {
                return (
                  <div 
                    key={index} 
                    className="file-card" 
                    tabIndex={0} 
                    role="button" 
                    aria-label={`Preview ${file.name}`} 
                    data-filename={file.name}
                    onClick={() => handleFilePreview(file)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFilePreview(file);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="file-name" title={file.name}>
                      {file.name}
                    </div>
                    <span className="file-type-label">{ext}</span>
                    {(() => {
                      const progress = getFileUploadProgress(file.name);
                      const isComplete = isFileUploadComplete(file.name);
                      const showOverlay = progress >= 1 && (!areAllUploadsComplete() || progress < 100);
                      return showOverlay && (
                        <FileProgressOverlay 
                          progress={progress} 
                          isComplete={isComplete} 
                          fileName={file.name} 
                        />
                      );
                    })()}
                    <button
                      className="file-card-close-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      ×
                    </button>
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        file={previewFile}
        chatId={appState.persistentChatId}
      />

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt,.csv,.json,.xml,.yaml,.yml,.xls,.xlsx,.ppt,.pptx,image/*"
      />


    </div>
  );
};

export default LandingView; 